/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { AgentActions } from './agent.actions';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
} from 'firebase/firestore';
import { FIRESTORE } from 'src/app/common/firebase/firebase.tokens';
import { Agent } from '@enrollhere/classes';
import { Action } from '@ngrx/store';

/**
 * @deprecated NgRx effects with direct Firestore access are deprecated. Use API endpoints instead.
 */
@Injectable()
export class AgentEffects {
  private actions$ = inject(Actions);
  /** @deprecated Direct Firestore access is deprecated. Use API endpoints instead. */
  private firestore = inject(FIRESTORE);

  private toError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error));
  }

  private mapAgentFromDoc(docSnapshot: {
    id: string;
    data: () => Record<string, unknown>;
  }) {
    return Agent.fromJSON({ ...docSnapshot.data(), id: docSnapshot.id });
  }

  private emitLoadAgentsSuccess(
    subscriber: { next: (action: Action) => void },
    snapshot: {
      docs: Array<{ id: string; data: () => Record<string, unknown> }>;
    }
  ) {
    const agents = snapshot.docs.map(docSnapshot =>
      this.mapAgentFromDoc(docSnapshot)
    );
    subscriber.next(AgentActions.loadAgentsSuccess({ agents }));
  }

  private emitLoadAgentsFailure(
    subscriber: { next: (action: Action) => void },
    error: unknown
  ) {
    subscriber.next(
      AgentActions.loadAgentsFailure({ error: this.toError(error) })
    );
  }

  private emitLoadAgentSuccess(
    subscriber: { next: (action: Action) => void },
    snapshot: { id: string; data: () => Record<string, unknown> }
  ) {
    const agent = Agent.fromJSON({
      ...snapshot.data(),
      id: snapshot.id,
    });
    subscriber.next(AgentActions.loadAgentSuccess({ agent }));
  }

  private emitLoadAgentFailure(
    subscriber: { next: (action: Action) => void },
    error: unknown
  ) {
    subscriber.next(
      AgentActions.loadAgentFailure({ error: this.toError(error) })
    );
  }

  private createAddAgentSuccess(agent: Agent, id: string) {
    return AgentActions.addAgentSuccess({
      agent: { ...agent, id },
    });
  }

  private createRemoveAgentSuccess(agentId: string) {
    return AgentActions.removeAgentSuccess({ agentId });
  }

  private createUpdateAgentSuccess(agentId: string, agent: Agent) {
    return AgentActions.updateAgentSuccess({
      agentId,
      agent,
    });
  }

  loadAgents$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgentActions.loadAgents),
      switchMap(() => {
        return new Observable<Action>(subscriber => {
          const unsubscribe = onSnapshot(
            collection(this.firestore, 'agents'),
            snapshot => {
              this.emitLoadAgentsSuccess(
                subscriber,
                snapshot as {
                  docs: Array<{
                    id: string;
                    data: () => Record<string, unknown>;
                  }>;
                }
              );
            },
            error => {
              this.emitLoadAgentsFailure(subscriber, error);
            }
          );

          // Provide a way of canceling and disposing the listener
          return unsubscribe;
        }).pipe(
          catchError(error =>
            of({ type: '[Agent API] Load Agents Error', error })
          )
        );
      })
    );
  });

  loadAgent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgentActions.loadAgent),
      switchMap(({ agentId }) => {
        return new Observable<Action>(subscriber => {
          const unsubscribe = onSnapshot(
            doc(this.firestore, 'agents', agentId),
            snapshot => {
              this.emitLoadAgentSuccess(
                subscriber,
                snapshot as {
                  id: string;
                  data: () => Record<string, unknown>;
                }
              );
            },
            error => {
              this.emitLoadAgentFailure(subscriber, error);
            }
          );
          return unsubscribe;
        }).pipe(
          catchError(error =>
            of({ type: '[Agent API] Load Agent Error', error })
          )
        );
      })
    );
  });

  addAgent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgentActions.addAgent),
      mergeMap(async ({ agent }) => {
        try {
          const docRef = await addDoc(
            collection(this.firestore, 'agents'),
            Agent.toJSON(agent) as any
          );
          return this.createAddAgentSuccess(agent, docRef.id); // return new agent with id
        } catch (error) {
          return AgentActions.addAgentFailure({ error: error as Error });
        }
      })
    );
  });

  removeAgent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgentActions.removeAgent),
      mergeMap(async ({ agentId }) => {
        try {
          await deleteDoc(doc(this.firestore, 'agents', agentId));
          return this.createRemoveAgentSuccess(agentId); // return removed agent's id
        } catch (error) {
          return AgentActions.removeAgentFailure({ error: error as Error });
        }
      })
    );
  });

  updateAgent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgentActions.updateAgent),
      mergeMap(async ({ agentId, agent }) => {
        try {
          await setDoc(
            doc(this.firestore, 'agents', agentId),
            Agent.toJSON(agent) as any,
            {
              merge: true,
            }
          );
          return this.createUpdateAgentSuccess(agentId, agent); // return updated agent's id and changes
        } catch (error) {
          return AgentActions.updateAgentFailure({ error: error as Error });
        }
      })
    );
  });
}
